import PageHeading from '../components/PageHeading';
import { ShoppingBag } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowRight } from 'lucide-react';
import { getOrderHistory } from '../api/payment';
import LoginModal from '../components/LoginModal';
import Toast from '../components/Toast';
import TokenManager from '../utils/tokenManager';

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function isOrderCompleted(order) {
  // 没 status 字段就当作已完成（当前 API 没返回，未来扩展时再细化）
  if (!order.status) return true;
  const s = String(order.status).toLowerCase();
  return s === 'completed' || s === 'success' || s === 'done' || s === '1';
}

// uid 可能是数字、邮箱字符串，也可能是多个账号的数组；空数组视为无身份
function hasUid(user) {
  const uid = user?.uid;
  if (Array.isArray(uid)) return uid.length > 0;
  return Boolean(uid);
}

// 依赖数组需要稳定值，避免数组每次渲染都被判为变化导致重复请求
function uidKey(user) {
  const uid = user?.uid;
  if (!uid) return '';
  return JSON.stringify(uid);
}

function Orders() {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useSelector((state) => state.user);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  // 需要登录验证：不清空本地用户数据，等用户重新登录后覆盖
  const [needsLogin, setNeedsLogin] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [toast, setToast] = useState(null);
  // 登录成功或点击重试时递增，触发重新拉取订单
  const [reloadKey, setReloadKey] = useState(0);

  // 已展示的订单保留，只弹出登录验证提示
  const requireLogin = () => {
    setError('');
    setLoading(false);
    setNeedsLogin(true);
    setShowSecurityModal(true);
  };

  useEffect(() => {
    // 没有登录信息或没有 access_token，不发请求，直接提示登录验证
    if (!isLoggedIn || !hasUid(user) || !TokenManager.getAccessToken()) {
      setOrders([]);
      requireLogin();
      return;
    }

    let cancelled = false;
    setNeedsLogin(false);
    setOrders([]);
    setLoading(true);
    setError('');

    const showOrders = (res) => {
      if (cancelled) return;
      const list = res?.data?.get_order_history;
      setOrders(Array.isArray(list) ? list : []);
      setLoading(false);
    };

    getOrderHistory({ uid: user.uid }, { onResponse: showOrders })
      .then((res) => {
        showOrders(res);
      })
      .catch((err) => {
        if (cancelled) return;

        // 401 / 403 / 认证类错误：统一提示登录验证，不展示技术错误
        const status = err.response?.status;
        if (status === 401 || status === 403 || err.code?.startsWith('AUTH_')) {
          requireLogin();
          return;
        }

        // 400 / 500 / 网络错误：温和提示并允许重试
        setError('订单暂时加载失败，请稍后重试');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, uidKey(user), reloadKey]);

  const handleLoginSuccess = () => {
    setIsLoginModalOpen(false);
    setShowSecurityModal(false);
    setNeedsLogin(false);
    setToast({ message: '登录成功，订单已刷新', type: 'success' });
    setReloadKey((k) => k + 1);
  };

  const handleOrderClick = (order) => {
    if (!isOrderCompleted(order) || !order.orderNo) return;
    navigate(`/detail/plus-upgrade?orderId=${encodeURIComponent(order.orderNo)}`);
  };

  return (
    <div className="min-h-screen bg-canvas">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* 安全提示弹窗 */}
      {showSecurityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
          <div className="responsive-dialog-panel bg-canvas rounded-2xl shadow-panel max-w-md w-full p-5 sm:p-8 border border-line">
            {/* 图标 */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-brand rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            </div>

            {/* 标题 */}
            <h2 className="text-2xl font-bold text-center text-ink mb-4">
              安全验证
            </h2>

            {/* 说明文字 */}
            <div className="space-y-3 mb-8">
              <p className="text-sm text-ink leading-relaxed">
                <span className="font-semibold text-red-600">近期发现黑客盗取卡密</span>，为保护您的资产安全，需要验证身份后才能查看历史订单。
              </p>
              <div className="bg-accent-soft border border-accent-line rounded-xl p-3">
                <p className="text-xs text-accent flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <span>如果没收到验证码，请查看邮箱垃圾箱</span>
                </p>
              </div>
            </div>

            {/* 按钮 */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  setShowSecurityModal(false);
                  navigate('/');
                }}
                className="flex-1 px-6 py-3 bg-section text-ink text-sm font-semibold rounded-xl hover:bg-gray-200 transition-all"
              >
                稍后再说
              </button>
              <button
                onClick={() => {
                  setShowSecurityModal(false);
                  setIsLoginModalOpen(true);
                }}
                className="flex-1 px-6 py-3 bg-brand text-white text-sm font-semibold rounded-xl hover:shadow-panel  transition-all"
              >
                去登录验证
              </button>
            </div>
          </div>
        </div>
      )}

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => {
          setIsLoginModalOpen(false);
          // 关闭登录弹窗后，如果仍未登录，重新显示安全提示
          if (needsLogin || !isLoggedIn) {
            setShowSecurityModal(true);
          }
        }}
        onLogin={handleLoginSuccess}
      />

      <div className="container mx-auto px-4 py-10 max-w-4xl">

        <PageHeading Icon={ShoppingBag} eyebrow="ORDERS · 购买记录" title="历史订单" description="查看支付状态，领取卡密、账号与网页入口。" />

        {needsLogin && orders.length === 0 ? (
          <div className="bg-brand-soft border border-brand-line rounded-2xl p-6 sm:p-12 md:p-16 text-center shadow-lg">
            <p className="text-xl font-bold text-ink mb-2">请先登录验证</p>
            <p className="text-sm text-muted mb-8">为保护您的资产安全，登录后即可查看历史订单</p>
            <button
              onClick={() => setIsLoginModalOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand text-white text-sm font-semibold rounded-xl hover:shadow-panel transition-all"
            >
              去登录验证
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : loading ? (
          <div className="bg-white border border-line rounded-2xl p-6 sm:p-12 md:p-16 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-black mx-auto mb-3"></div>
            <p className="text-sm text-muted">加载中…</p>
          </div>
        ) : error && orders.length === 0 ? (
          <div className="bg-white border border-line rounded-2xl p-6 sm:p-12 md:p-16 text-center">
            <p className="text-xl font-bold text-ink mb-2">订单暂时加载失败</p>
            <p className="text-sm text-muted mb-8">网络或服务繁忙，请稍后重试</p>
            <button
              onClick={() => setReloadKey((k) => k + 1)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand text-white text-sm font-semibold rounded-xl hover:bg-brand-hover transition-colors"
            >
              重新加载
            </button>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white border border-line rounded-2xl p-6 sm:p-12 md:p-16 text-center">
            <p className="text-xl font-bold text-ink mb-2">暂无订单记录</p>
            <p className="text-sm text-muted mb-8">你还没有购买过任何套餐</p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand text-white text-sm font-semibold rounded-xl hover:bg-brand-hover transition-colors"
            >
              去选购套餐
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => {
              const productName = order.product_name || '商品已下架';
              const priceUsd = order.product_price_usd;
              const completed = isOrderCompleted(order);
              const clickable = completed && Boolean(order.orderNo);
              return (
                <div
                  key={order.orderNo || order.out_trade_no}
                  onClick={() => clickable && handleOrderClick(order)}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      handleOrderClick(order);
                    }
                  }}
                  className={`bg-white border rounded-2xl p-5 transition-all ${
                    clickable
                      ? 'border-line hover:border-brand-line hover:shadow-panel cursor-pointer group'
                      : 'border-line opacity-60'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="min-w-0 pr-4 flex-1">
                      <h3 className="text-base font-bold text-ink mb-1 break-words">{productName}</h3>
                      {order.orderNo && (
                        <p className="text-xs text-muted font-mono break-all">订单号 {order.orderNo}</p>
                      )}
                      {order.out_trade_no && (
                        <p className="text-xs text-muted font-mono break-all mt-0.5">交易号 {order.out_trade_no}</p>
                      )}
                    </div>
                    <span className={`shrink-0 px-3 py-1 rounded-full text-xs font-semibold border ${
                      completed
                        ? 'text-sage-700 bg-sage-50 border-sage-200'
                        : 'text-muted bg-canvas border-line'
                    }`}>
                      {completed ? '已完成' : '未完成'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-6 text-sm border-t border-line pt-4">
                    <div>
                      <p className="text-xs text-muted mb-1">订单金额</p>
                      <p className="text-ink font-semibold">
                        {priceUsd ? `$${priceUsd}` : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted mb-1">下单时间</p>
                      <p className="text-ink">{formatDateTime(order.created_at)}</p>
                    </div>
                  </div>

                  {clickable && (
                    <div className="mt-3 pt-3 border-t border-line flex items-center justify-end gap-1 text-xs font-semibold text-muted group-hover:text-ink transition-colors">
                      <span>查看详情</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Orders;
